import React, { useRef, useState, useMemo, useCallback } from "react";
import { CheckCircleOutlined } from "@ant-design/icons";
import { List, Typography, Button, Modal, Form, Row, Col, Input, Spin } from "antd";
import { ShipmentStatus, PricingEngineResultStatus, QuoteStatus, QuoteStatusMap } from "@/utils/enum";
import { formatPrice } from "@/utils/utils";
import { acceptPricingEnginePricingResult } from "@/services/quotes_new";
import AlertWithBorder from "@/components//AlertWithBorder";
import VehicleDescription from "@/components/VehicleDescription";
import VehicleAdjustQuote from "../VehicleDescription/VehicleAdjustQuote";
import styles from "./VehiclesList.styles.less";

const { Item } = List;
const { Text } = Typography;
const { TextArea } = Input;

const VehicleItem = ({
  configPerm,
  item,
  updatedQuotes,
  compact,
  highlightVin,
  prices,
  shipmentData,
  multiPrice,
  multiPriceTitles,
  itemExtra,
  descriptionColumn,
  pricesSpacing,
  onNotesUpdate,
  onQuoteUpdate,
}) => {
  const acceptPricingResultsFormRef = useRef(null);

  const [acceptQuoteModalOpen, setAccepetQuoteModalOpen] = useState(false);
  const [acceptReasonLoading, setAcceptReasonLoading] = useState(false);
  const [acceptLoading, setAcceptLoading] = useState(false);

  const [quoteResponse, setQuoteResponse] = useState(null);

  const quote = useMemo(() => {
    return quoteResponse || updatedQuotes?.[item.id] || item.quote || null;
  }, [quoteResponse, updatedQuotes, item?.id, item.quote]);

  const handleCloseAskingForReasonModal = useCallback(() => {
    acceptPricingResultsFormRef?.current?.resetFields();
    setAccepetQuoteModalOpen(null);
    setAcceptReasonLoading(false);
  }, []);

  const acceptPricingEngineResult = useCallback(
    async reason => {
      const { id: quoteId } = quote;
      setAcceptLoading(true);

      let responseError, response;

      try {
        response = await acceptPricingEnginePricingResult(shipmentData.id, quoteId, reason);

        if (response !== undefined) {
          setQuoteResponse(response);

          if (typeof onQuoteUpdate === "function") {
            onQuoteUpdate(response);
          }

          if (
            typeof onNotesUpdate === "function" &&
            item.pricingEngineResult.type !== PricingEngineResultStatus.Success
          ) {
            onNotesUpdate();
          }
        } else {
          responseError = {
            error: "Something went wrong",
          };
        }
      } catch (error) {
        responseError = error;
      }

      setAcceptLoading(false);

      return {
        data: response,
        error: responseError,
      };
    },
    [item?.pricingEngineResult?.type, shipmentData?.id, quote, onQuoteUpdate, onNotesUpdate],
  );

  const handleAcceptPricingEngineWithReason = useCallback(() => {
    acceptPricingResultsFormRef?.current?.validateFields?.()?.then(formResponse => {
      setAcceptReasonLoading(true);

      acceptPricingEngineResult(formResponse.reason)
        .then(response => {
          if (!response.error) {
            handleCloseAskingForReasonModal();
          }
        })
        .finally(() => {
          setAcceptReasonLoading(false);
        });
    });
  }, [acceptPricingEngineResult, handleCloseAskingForReasonModal]);

  const rightSidePricingEngineResult = useCallback(() => {
    if (!item.pricingEngineResult) {
      return null;
    }

    let message;
    let type;
    const alertButtons = [];

    switch (item.pricingEngineResult.type) {
      case PricingEngineResultStatus.Success: {
        message = "Success";
        type = "success";
        break;
      }

      case PricingEngineResultStatus.Warning: {
        message = "Warning";
        type = "warning";
        break;
      }

      case PricingEngineResultStatus.Error: {
        message = "Error";
        type = "error";
        break;
      }
    }

    if (configPerm["PricingService.QuoteRuleResults"]) {
      const params = new URLSearchParams({
        id: item.pricingEngineResult.id,
        tab: type,
        shipmentId: shipmentData?.id,
      });

      alertButtons.push({
        label: "Review",
        key: "review-pricing-engine-results",
        href: `/admin/shipments/view/messages?${params.toString()}`,
      });
    }

    return (
      <Spin spinning={acceptLoading}>
        <div className={styles.pricingEngineResultWrapper}>
          <AlertWithBorder message={message} type={type} actionButtons={alertButtons} />
          <div style={{ display: "flex", flexDirection: "column" }}>
            <div className={styles.pricingResultWrapper}>
              <div style={{ width: "100%" }}>
                <Text style={{ fontWeight: "normal", fontSize: "12px" }}>Dealer price</Text>
                <br />
                <Text strong>{formatPrice(item.pricingEngineResult.dealerPrice)}</Text>
              </div>
              <div style={{ width: "100%" }}>
                <Text style={{ fontWeight: "normal", fontSize: "12px" }}>Transporter price</Text>
                <br />
                <Text strong>{formatPrice(item.pricingEngineResult.transporterPrice)}</Text>
              </div>
              {configPerm["CoreService.AdminShipments.Vehicles.AdjustQuote"] && (
                <VehicleAdjustQuote
                  alertMessage={message}
                  alertType={type}
                  quoteData={item.pricingEngineResult}
                  onNotesUpdate={onNotesUpdate}
                  onQuoteUpdate={onQuoteUpdate}
                />
              )}
            </div>
            {configPerm["CoreService.AdminShipments.Vehicles.Accept"] && (
              <div className={styles.acceptQuoteWrapper}>
                <Button
                  type="primary"
                  className={styles.acceptQuoteButton}
                  onClick={() => {
                    if (item.pricingEngineResult.type !== PricingEngineResultStatus.Success) {
                      setAccepetQuoteModalOpen(true);
                    } else {
                      acceptPricingEngineResult();
                    }
                  }}
                >
                  Accept quote
                </Button>
              </div>
            )}
          </div>
        </div>
      </Spin>
    );
  }, [item, acceptLoading, configPerm, shipmentData, acceptPricingEngineResult]);

  const rightSidePriceMissing = useCallback(() => {
    return (
      <div className={styles.rightSideWrapper}>
        <div style={{ color: "rgba(0, 0, 0, 0.85)" }}>Status</div>
        <Text>Missing</Text>
      </div>
    );
  }, []);

  const rightSideSinglePrice = useCallback(() => {
    const isShipment = shipmentData && quote;
    const completed = !isShipment || (isShipment && quote?.status === QuoteStatus.Completed);
    return (
      <div className={styles.rightSideWrapper}>
        <div style={{ color: "rgba(0, 0, 0, 0.85)" }}>{completed ? "Price" : "Status"}</div>
        {completed ? (
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            {quote?.status === QuoteStatus.Completed && shipmentData?.status <= ShipmentStatus.Pending && (
              <CheckCircleOutlined style={{ color: "#52c41a", marginRight: 5, fontSize: 16 }} />
            )}

            <Text>${prices[item.id]}</Text>
          </div>
        ) : (
          <Text>{QuoteStatusMap[quote?.status]}</Text>
        )}
      </div>
    );
  }, [shipmentData, quote]);

  const rightSideMultiPrice = useCallback(() => {
    return (
      <div className={styles.multiPriceWrapper}>
        {prices[item.id].map((_value, index) => {
          let price = prices[item.id][index];

          if (typeof price !== "number" || isNaN(price)) {
            price = 0;
          }

          return (
            <div key={index} className={styles.singlePriceItem}>
              {multiPriceTitles[index] || "Price"}
              <Text>
                {/* Only show check circled icon if shipment is in process of being quoted */}
                {quote.status === QuoteStatus.Completed && shipmentData?.status <= ShipmentStatus.Pending && (
                  <CheckCircleOutlined style={{ color: "#52c41a", marginRight: 5, fontSize: 16 }} />
                )}
                {formatPrice(price)}
              </Text>
            </div>
          );
        })}
      </div>
    );
  }, [shipmentData, prices, multiPriceTitles]);

  const rightSide = useMemo(() => {
    if (item.pricingEngineResult && !updatedQuotes?.[item.id]) {
      return rightSidePricingEngineResult();
    } else if (quote?.status === QuoteStatus.None) {
      return rightSidePriceMissing();
    } else if (!multiPrice && typeof prices[item.id] === "number") {
      return rightSideSinglePrice();
    } else if (multiPrice && Array.isArray(prices[item.id])) {
      return rightSideMultiPrice();
    }

    return null;
  }, [
    updatedQuotes,
    multiPrice,
    prices,
    item,
    rightSidePricingEngineResult,
    rightSideSinglePrice,
    rightSideMultiPrice,
    rightSidePriceMissing,
  ]);

  return (
    <Item>
      <div style={{ display: "flex", flexDirection: "row" }}>
        <div className={compact ? styles.descriptionWrapperSmallSpace : styles.descriptionWrapper}>
          <VehicleDescription
            vehicle={{ ...item, shipment: shipmentData }}
            withTags={true}
            column={descriptionColumn}
            showBOLLink={shipmentData?.status >= ShipmentStatus.Delivered}
            quote={quote}
            {...{ highlightVin }}
          />
        </div>

        <div
          style={{
            display: "flex",
            flex: "0 1 0%",
            borderRight: "1px solid rgba(0, 0, 0, 0.1)",
            marginLeft: "8px",
            marginRight: "8px",
          }}
        />

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            flex: typeof pricesSpacing === "number" ? pricesSpacing : multiPrice ? 3 : 2,
          }}
        >
          {typeof itemExtra === "function" && itemExtra(item)}
          {rightSide}
        </div>
      </div>

      <Modal
        title="Validation Errors Detected"
        visible={acceptQuoteModalOpen}
        onOk={handleAcceptPricingEngineWithReason}
        onCancel={handleCloseAskingForReasonModal}
        confirmLoading={acceptReasonLoading}
      >
        <Form layout="vertical" ref={acceptPricingResultsFormRef} requiredMark={true}>
          <Row gutter={16}>
            <Col lg={24}>
              <p>
                Our system has identified potential pricing issues in this quote. You may proceed with this price, but
                we require a reason to accept it despite the validation errors.
              </p>
              <Form.Item name="reason" label="Reason" rules={[{ required: true }]}>
                <TextArea rows={4} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </Item>
  );
};

export default VehicleItem;
